import { logger } from "@/lib/logger";
import { ReactNode, Component, ErrorInfo, lazy, Suspense } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { MobileFooter } from "./MobileFooter";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { PageSiteProvider } from "@/contexts/PageSiteContext";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { Toaster } from "@/components/ui/toaster";
import { useIsMobile, useIsTablet } from "@/hooks/use-mobile";
import { CookieConsentBanner } from "@/components/consent/CookieConsentBanner";
import { SkipToContent } from "@/components/seo/SkipToContent";
import { InstallAppButton } from "@/components/mobile/InstallAppButton";
import { MobileAuthPrompt } from "@/components/mobile/MobileAuthPrompt";
import { ContextSwitchHint } from "@/components/onboarding/ContextSwitchHint";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import { OfflineQueueStatus } from "@/components/mobile/OfflineQueueStatus";
import { SupabaseUnreachableBanner } from "@/components/system/SupabaseUnreachableBanner";

// Lazy load heavy components that are not critical for initial render
const UnifiedHelpWidget = lazy(() => import("@/components/help/UnifiedHelpWidget").then(m => ({ default: m.UnifiedHelpWidget })));
const RoleBasedOnboarding = lazy(() => import("@/components/onboarding/RoleBasedOnboarding").then(m => ({ default: m.RoleBasedOnboarding })));
const PWAInstallPrompt = lazy(() => import("@/components/mobile/PWAInstallPrompt").then(m => ({ default: m.PWAInstallPrompt })));

interface LayoutProps {
  children: ReactNode;
  showNavigation?: boolean;
  showFooter?: boolean;
  showSupportChat?: boolean;
  noPadding?: boolean;
  showComplianceBadges?: boolean;
}

/**
 * Silent Error Boundary for OfflineQueueStatus
 * If the offline indicator crashes, we just hide it - don't crash the whole app
 */
interface SilentErrorBoundaryProps {
  children: ReactNode;
}

interface SilentErrorBoundaryState {
  hasError: boolean;
}

class SilentErrorBoundary extends Component<SilentErrorBoundaryProps, SilentErrorBoundaryState> {
  constructor(props: SilentErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SilentErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log but don't crash the app
    logger.warn('[SilentErrorBoundary] Caught error in offline component:', error.message);
    logger.warn('[SilentErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Render nothing if there's an error - silent fail
      return null;
    }
    return this.props.children;
  }
}

export function Layout({ 
  children, 
  showNavigation = true, 
  showFooter = true,
  showSupportChat = true,
  noPadding = false,
  showComplianceBadges = true
}: LayoutProps) {
  // Use centralized auth context - no more local state!
  const { user, userProfile, loading } = useAuth();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();


  // Clean layout without sidebar - much simpler!
  return (
    <PageSiteProvider>
    <OrganizationProvider>
      {/* Always-visible connection status indicator - wrapped in error boundary */}
      {user && (
        <SilentErrorBoundary>
          <OfflineQueueStatus />
        </SilentErrorBoundary>
      )}
      <div className="min-h-screen bg-background flex flex-col overflow-x-hidden pb-20 md:pb-0">
        <SilentErrorBoundary>
          <SupabaseUnreachableBanner />
        </SilentErrorBoundary>
        <SkipToContent />
        <MobileAuthPrompt />
        <ContextSwitchHint />
        
        {/* Mobile: Simple header, Desktop: Full navigation */}
        {isMobile ? (
          showNavigation && <MobileHeader />
        ) : (
          showNavigation && <Navigation user={user} userProfile={userProfile} />
        )}
        
        {user && !isMobile && (
          <div className="fixed z-50 top-20 right-6">
            <NotificationCenter />
          </div>
        )}
        
        <main 
          id="main-content" 
          className={`flex-1 ${noPadding ? '' : 'p-4 sm:p-6'}`}
          tabIndex={-1}
        >
          {children}
        </main>
        
        {/* Footer: Desktop gets full footer, Tablet gets mobile footer, Phone gets bottom nav only */}
        {showFooter && !isMobile && !isTablet && <Footer showComplianceBadges={showComplianceBadges} />}
        {showFooter && isTablet && <MobileFooter showComplianceBadges={showComplianceBadges} />}
        
        {/* Mobile bottom navigation */}
        {isMobile && <MobileBottomNav />}
        
        {!noPadding && <div className="py-6" />}
        <CookieConsentBanner />
        <Suspense fallback={null}>
          <PWAInstallPrompt />
        </Suspense>
        <InstallAppButton />
        <Toaster />
        
        {/* Unified help widget: one bubble, "This page" + "Assistant" tabs */}
        {showSupportChat && (
          <Suspense fallback={null}>
            <UnifiedHelpWidget />
          </Suspense>
        )}
        {user && (
          <Suspense fallback={null}>
            <RoleBasedOnboarding />
          </Suspense>
        )}
      </div>
    </OrganizationProvider>
    </PageSiteProvider>
  );
}